import { request } from './client';
import type {
  DocumentPage,
  DocumentRow,
  Upload,
  WellTags,
} from '../types';

/** Documents: listing, uploading, tagging, downloading. */
export const documentsApi = {
  /**
   * One page of a folder's documents. For a smart folder the server runs its
   * rule instead of reading rows, which the client never has to know.
   */
  list: (folderId: number, page: number, perPage: number) =>
    request<DocumentPage>(`/folders/${folderId}/documents?page=${page}&perPage=${perPage}`),

  /** A well's documents carrying one tag, from wherever they are filed. */
  listByTag: (wellId: number, tag: string, page: number, perPage: number) =>
    request<DocumentPage>(
      `/wells/${wellId}/documents?tag=${encodeURIComponent(tag)}&page=${page}&perPage=${perPage}`,
    ),

  /** The tags a well's documents carry, with counts, and the ones to offer. */
  tags: async (wellId: number): Promise<WellTags> => {
    const res = await request<Partial<WellTags>>(`/wells/${wellId}/document-tags`);
    return { items: res.items ?? [], suggestions: res.suggestions ?? [] };
  },

  /**
   * Replace a document's tags; an empty list clears them. The server keeps
   * the spelling the company already uses, so what comes back is what to show.
   */
  setTags: (documentId: number, tags: string[]) =>
    request<DocumentRow>(`/documents/${documentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ tags }),
    }),

  /**
   * Step one of an upload: the API creates the row and signs a URL. It never
   * sees the file itself.
   */
  startUpload: (folderId: number, body: { name: string; contentType: string }) =>
    request<Upload>(`/folders/${folderId}/documents`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Step three: the bytes are in object storage, so the row becomes visible. */
  completeUpload: (documentId: number, sizeBytes: number) =>
    request<DocumentRow>(`/documents/${documentId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ sizeBytes }),
    }),

  /** A short-lived signed URL. Requires object storage to be configured. */
  downloadUrl: (documentId: number) =>
    request<{ url: string; expiresAt: string }>(`/documents/${documentId}/download`),
};
